/**
 * The button.
 *
 * Replaces twenty-two families that shared an anatomy and agreed on nothing
 * else, and is drawn to the Vesper canvas. Three axes:
 *
 *   variant  how much weight it carries   filled | outlined | ghost | link | inline
 *   tone     what it means                neutral | primary | danger
 *   size     sm | md | lg
 *
 * Tone rides on top of variant rather than replacing it, so a danger ghost is
 * still a ghost — which is what the cancel-a-flight control needs and what a
 * flat variant list could not express.
 *
 * `link` is the lightest: words alone, in the accent's ink and the label's
 * lettering — a quiet action beside a field's label, the route builder's
 * Change. It takes no tone and no detail. `inline` is words inside a
 * sentence: the sentence's own lettering in the accent's ink, underlined — a
 * way on that is part of what the words say, the full-screen gate's
 * "continue without it". It takes no tone, size or detail of its own.
 *
 * `filled` + `primary` is gold: the one decisive action on a screen. It marks
 * itself `data-action="decisive"`, which utils/decisive-action-watch.ts counts
 * in development, warning when a screen shows two — so reach for it once per
 * screen, not per panel. `outlined` + `primary` is the quieter gold of
 * something that needs you.
 *
 * `detail` is the second, quieter half of a label: the price on a line button
 * ("Buy · $20.0M"), the caption on a ghost ("+ New sub-fleet · 7 more
 * possible"). With a detail the button spreads its content to both ends, and a
 * detail with no room beside the label drops under it, at the far end. It exists
 * only on the outlined and ghost treatments — on a filled fill its caption ink
 * would fall below AA — and the type says so.
 *
 * `reason` is why the button cannot act now. It rests rather than disabling:
 * the disabled look, but it keeps the pointer and focus, swallows the press,
 * and hangs the reason from itself in a hover card — on hover, focus and a
 * long press — while assistive technology reads it as the button's
 * description at all times. `disabled` is for a button with nothing to
 * explain.
 *
 *   <Button variant="outlined" reason="All 2 are flying. Take one off a route to sell it.">Sell</Button>
 *
 * A reason can also be a state with a name — `{ title, status, tone, note }` —
 * when a few words say more than a sentence alone: the card carries the
 * button's name over its keyline, the state (coral when something stands in
 * the way, plain when it is only not ready yet) and the sentence under it, as
 * the route builder's Add flight does. `reasonPlacement` hangs the card on
 * another side than below: a button at the foot of a docked panel hangs it
 * to the left, over the globe. A button given `reason` at all — even as
 * `undefined`, while it can act — always wears the card, shut while there is no
 * reason, so the button is the same element whether it rests or not and keeps
 * the focus as its reason comes and goes.
 *
 * `busy` is the press being worked on. The button keeps its look — the gold
 * stays gold — with a spinner before a label that says what is under way
 * ("Adding…"), and holds still, the press going nowhere, until the answer
 * comes.
 *
 * `glyph` leads the label with a drawing — Add flight's route, Remove's bin —
 * kept beside the words when a detail drops under them.
 *
 * NOT for icon-only buttons. A round control with a glyph and no label is a
 * different anatomy; that is `IconButton`.
 */

import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';

import { GLYPH, type Glyph } from './glyphs';
import { HoverCard, HoverCardNote, HoverCardStatus, HoverCardTitle } from './hover-card';
import { Icon } from './icon';
import { Spinner } from './spinner';
import type { Side } from '../../utils/clamp-to-viewport';
import styles from './button.module.css';

const VARIANT = {
  filled: styles.filled,
  outlined: styles.outlined,
  ghost: styles.ghost,
  link: styles.link,
  inline: styles.inline,
} as const;

const TONE = {
  neutral: '',
  primary: styles.primary,
  danger: styles.danger,
} as const;

const SIZE = {
  sm: styles.sm,
  md: styles.md,
  lg: styles.lg,
} as const;

/** Why a button rests, when its state has a name: "Out of range", over why. */
export interface ButtonReason {
  /** The button's name, over the card's keyline. */
  title: string;
  /** The state, in a few words. */
  status: string;
  /** Coral when something stands in the way; plain when it is only not ready yet. */
  tone: 'danger' | 'neutral';
  /** Why, and what would change it. */
  note?: string;
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: keyof typeof TONE;
  size?: keyof typeof SIZE;
  /** Fill the width of its container, for a button that ends a modal column. */
  block?: boolean;
  /** For a Popover or HoverCard that hangs from this button. */
  ref?: Ref<HTMLButtonElement>;
  /** Why it cannot act now: it rests, and says so on hover and focus and to assistive technology. */
  reason?: string | ButtonReason;
  /** Where the reason's card hangs: below, unless the button stands where that has no room. */
  reasonPlacement?: Side;
  /** The press is being worked on: a spinner, and the press goes nowhere until the answer comes. */
  busy?: boolean;
  /** A drawing before the words, kept beside them. */
  glyph?: Glyph;
} & (
    | {
        variant: 'outlined' | 'ghost';
        /** The quieter second half of the label: a price, a count, a caption. */
        detail?: ReactNode;
      }
    | { variant?: 'filled' | 'link' | 'inline'; detail?: never }
  );

export function Button(props: ButtonProps) {
  const {
    variant = 'filled',
    tone = 'neutral',
    size = 'md',
    block = false,
    detail,
    reason,
    reasonPlacement = 'below',
    busy = false,
    glyph,
    className = '',
    type = 'button',
    onClick,
    children,
    ...rest
  } = props;
  // Working on a press, it waits for the answer; resting, it cannot act.
  const resting = !busy && reason !== undefined;
  // Filled and primary is the gold. A resting gold button is not the screen's
  // decisive action: it cannot act. A busy one still is — it is the action,
  // under way.
  const gold = variant === 'filled' && tone === 'primary';
  const decisive = gold && !resting;
  const button = (
    <button
      type={type}
      data-action={decisive ? 'decisive' : undefined}
      className={[
        styles.button,
        // Gold is its own treatment, not the plate tinted (button.module.css).
        gold ? styles.gold : VARIANT[variant],
        TONE[tone],
        SIZE[size],
        block ? styles.block : '',
        detail != null ? styles.withDetail : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
      aria-busy={busy || undefined}
      // Resting or busy overrides what the caller passed: the press goes nowhere.
      {...(resting || busy ? { 'aria-disabled': true, onClick: undefined } : { onClick })}
    >
      {busy && <Spinner size={size === 'sm' ? 'sm' : 'md'} tone={gold ? 'ink' : 'soft'} />}
      {glyph && !busy ? (
        <span className={styles.lead}>
          <Icon glyph={glyph} size="sm" />
          {children}
        </span>
      ) : (
        children
      )}
      {detail != null && <span className={styles.detail}>{detail}</span>}
    </button>
  );
  // A button that can rest wears its card always, so resting never remounts it.
  if (!('reason' in props)) return button;
  if (!resting || reason === undefined) {
    return (
      <HoverCard placement={reasonPlacement} stretch={block} card={null} suppressed>
        {button}
      </HoverCard>
    );
  }
  // Read out whole, whether or not the card is open: the state, then why.
  const said = typeof reason === 'string' ? reason : [reason.status, reason.note].filter(Boolean).join('. ');
  const card =
    typeof reason === 'string' ? (
      <HoverCardNote>{reason}</HoverCardNote>
    ) : (
      <>
        <HoverCardTitle>{reason.title}</HoverCardTitle>
        <HoverCardStatus tone={reason.tone}>
          <Icon glyph={reason.tone === 'danger' ? GLYPH.warning : GLYPH.info} size="sm" />
          {reason.status}
        </HoverCardStatus>
        {reason.note && <HoverCardNote>{reason.note}</HoverCardNote>}
      </>
    );
  return (
    <HoverCard label={said} description={said} placement={reasonPlacement} stretch={block} card={card}>
      {button}
    </HoverCard>
  );
}
